import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import api from "../../lib/api";
import { adminError } from "../../lib/jamoraAdmin";

// The short administration session (30 min idle, 8 h at most): opened by confirming the CRM password, kept by the server in an httpOnly cookie.
// This context only knows whether it is open and when it ends — never a token.
const Ctx = createContext(null);
export const useAdminSession = () => useContext(Ctx);

export function AdminSessionProvider({ children }) {
  const [phase, setPhase] = useState("loading"); // loading | ready | error
  const [session, setSession] = useState({ active: false });
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/jamora-admin/status");
      if (!alive.current) return;
      setSession(data.session || { active: false });
      setPhase("ready");
    } catch {
      if (alive.current) setPhase("error");
    }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const open = useCallback(async (password) => {
    const { data } = await api.post("/jamora-admin/session", { password });
    setSession(data.session);
    return data.session;
  }, []);

  const close = useCallback(async () => {
    try { await api.delete("/jamora-admin/session"); } catch { /* the cookie goes anyway at expiry */ }
    setSession({ active: false });
  }, []);

  // A successful read has just moved the server's inactivity clock: mirror it so the countdown stays honest.
  const touch = useCallback(() => {
    setSession((current) => (current.active ? { ...current, idle_expires_at: new Date(Date.now() + (current.idle_minutes || 30) * 60000).toISOString() } : current));
  }, []);
  const expire = useCallback(() => setSession({ active: false }), []);

  const value = useMemo(() => ({ phase, session, open, close, touch, expire, refresh }), [phase, session, open, close, touch, expire, refresh]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export { adminError };
